import React from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Badge,
  useColorModeValue,
} from "@chakra-ui/react";
import { TiltCard } from "../../common/TiltCard";
import { usePublicGlassSurface } from "../../common/publicVisual";

/**
 * Shared layout for FAQ, Contact, Privacy, Terms — hero band + readable content width.
 */
export function LegalPageShell({
  badge,
  title,
  intro,
  children,
  lastUpdated,
  maxW = "min(100%, 760px)",
}) {
  const bandBg = useColorModeValue(
    "linear-gradient(135deg, #0f766e 0%, #115e59 45%, #134e4a 100%)",
    "linear-gradient(135deg, #115e59 0%, #0f172a 55%, #134e4a 100%)"
  );
  const glass = usePublicGlassSurface();
  const muted = useColorModeValue("gray.500", "gray.400");

  return (
    <Box
      as="main"
      bg="transparent"
      minH="75vh"
      maxW="100vw"
      overflowX="hidden"
      pb="calc(1rem + env(safe-area-inset-bottom, 0px))"
    >
      <Box
        bg={bandBg}
        color="white"
        pt={{ base: 10, md: 14 }}
        pb={{ base: 14, md: 20 }}
        px={{ base: "max(1rem, env(safe-area-inset-left, 0px))", md: 4 }}
        pr={{ base: "max(1rem, env(safe-area-inset-right, 0px))", md: 4 }}
        position="relative"
        overflow="hidden"
        sx={{
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            bgGradient: "linear(to bottom right, rgba(255,255,255,0.12), transparent 45%, rgba(0,0,0,0.08))",
            pointerEvents: "none",
          },
        }}
      >
        <Container maxW="container.lg" textAlign={{ base: "left", md: "center" }} position="relative" zIndex={1}>
          {badge ? (
            <Badge
              colorScheme="teal"
              variant="solid"
              px={3}
              py={1}
              borderRadius="full"
              fontSize="xs"
              letterSpacing="wide"
              mb={4}
              bg="whiteAlpha.200"
              color="teal.50"
            >
              {badge}
            </Badge>
          ) : null}
          <Heading as="h1" fontSize={{ base: "2xl", md: "4xl" }} fontWeight="extrabold" letterSpacing="tight">
            {title}
          </Heading>
          {intro ? (
            <Text
              mt={4}
              fontSize={{ base: "md", md: "lg" }}
              color="whiteAlpha.900"
              maxW="680px"
              mx={{ base: 0, md: "auto" }}
              lineHeight="tall"
            >
              {intro}
            </Text>
          ) : null}
        </Container>
      </Box>

      <Container maxW="container.lg" px={{ base: 4, md: 6 }} pb={16} mt={{ base: -8, md: -10 }}>
        <TiltCard
          tilt={5}
          borderRadius="2xl"
          borderWidth="1px"
          maxW={maxW}
          mx="auto"
          px={{ base: 5, md: 10 }}
          py={{ base: 8, md: 10 }}
          sx={{ overflowWrap: "anywhere", wordBreak: "break-word", transformStyle: "preserve-3d" }}
          {...glass}
        >
          <VStack spacing={0} align="stretch">
            {children}
            {lastUpdated ? (
              <Text
                mt={10}
                pt={6}
                borderTopWidth="1px"
                borderColor={glass.borderColor}
                fontSize="sm"
                color={muted}
              >
                {lastUpdated}
              </Text>
            ) : null}
          </VStack>
        </TiltCard>
      </Container>
    </Box>
  );
}

export function LegalSection({ title, children, id }) {
  const hColor = useColorModeValue("teal.700", "teal.200");
  const body = useColorModeValue("gray.700", "gray.200");

  return (
    <Box
      as="section"
      id={id}
      pt={8}
      sx={{ "&:first-of-type": { paddingTop: 0 } }}
    >
      <Heading as="h2" size="md" color={hColor} mb={4} fontWeight="bold">
        {title}
      </Heading>
      <Box color={body} fontSize="md" lineHeight="tall">
        {children}
      </Box>
    </Box>
  );
}
